import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

const PROVIDERS = [
  { id: 'openai', label: 'OpenAI' },
  { id: 'gemini', label: 'Gemini' },
  { id: 'claude', label: 'Claude' },
  { id: 'deepseek', label: 'DeepSeek' },
  { id: 'kimi', label: 'Kimi' },
  { id: 'glm', label: 'GLM' },
];

const DEFAULT_CHECKED = {
  openai: ['gpt-4o-mini', 'gpt-4o'],
  gemini: ['gemini-2.5-flash', 'gemini-2.5-pro'],
  claude: ['claude-sonnet-5', 'claude-opus-5'],
  deepseek: ['deepseek-v4-flash', 'deepseek-v4-pro'],
  kimi: ['kimi-k2.6', 'kimi-k3'],
  glm: ['glm-4.7-flash', 'glm-5.2'],
};

function emptyLists() {
  return {
    openai: [],
    gemini: [],
    claude: [],
    deepseek: [],
    kimi: [],
    glm: [],
  };
}

function normalizeEnabled(value) {
  const src = value && typeof value === 'object' ? value : {};
  const list = (raw) =>
    Array.isArray(raw)
      ? raw.map((id) => String(id).trim()).filter(Boolean)
      : [];
  return {
    openai: list(src.openai),
    gemini: list(src.gemini),
    claude: list(src.claude),
    deepseek: list(src.deepseek),
    kimi: list(src.kimi),
    glm: list(src.glm),
  };
}

export default function HostedAIAvailableModels({
  enabledModels,
  onChange,
  providerApiKeysConfigured,
  typedKeys,
  expanded,
  refreshToken = 0,
}) {
  const saved = useMemo(() => normalizeEnabled(enabledModels), [enabledModels]);
  const configured = providerApiKeysConfigured || {};
  const typed = typedKeys || {};

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [catalog, setCatalog] = useState(null);
  const [filter, setFilter] = useState({
    openai: '',
    gemini: '',
    claude: '',
    deepseek: '',
    kimi: '',
    glm: '',
  });
  const [loadedOnce, setLoadedOnce] = useState(false);

  const canLoad = PROVIDERS.some(
    (provider) =>
      Boolean(typed[provider.id] && typed[provider.id].trim()) || Boolean(configured[provider.id])
  );

  const loadCatalog = useCallback(async (forceRefresh = false) => {
    if (!canLoad) {
      setCatalog(null);
      setError('Save a provider API key above, then load models.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const keys = {};
      PROVIDERS.forEach((provider) => {
        if (typed[provider.id] && typed[provider.id].trim()) {
          keys[provider.id] = typed[provider.id].trim();
        }
      });
      const response = await fetch('/api/config/feature-pricing/hosted-provider-models', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          useStored: true,
          keys,
          forceRefresh: Boolean(forceRefresh),
        }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to load provider models');
        return;
      }
      setCatalog(data.providers || {});
      setLoadedOnce(true);
    } catch (e) {
      setError(e.message || 'Failed to load provider models');
    } finally {
      setLoading(false);
    }
  }, [canLoad, typed.openai, typed.gemini, typed.claude, typed.deepseek, typed.kimi, typed.glm]);

  useEffect(() => {
    if (!refreshToken) return;
    loadCatalog(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  useEffect(() => {
    if (!expanded || loadedOnce) return;
    if (!PROVIDERS.some((provider) => configured[provider.id])) return;
    loadCatalog();
    // Auto-load only when the section opens and a stored key exists. Typed
    // unsaved keys are applied on explicit Load/Refresh so we don't fetch on
    // every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    expanded,
    loadedOnce,
    configured.openai,
    configured.gemini,
    configured.claude,
    configured.deepseek,
    configured.kimi,
    configured.glm,
  ]);

  const hasSavedSelection = PROVIDERS.some((provider) => (saved[provider.id] || []).length > 0);

  const selectedFor = (provider) => {
    const current = saved[provider] || [];
    if (hasSavedSelection) return new Set(current);
    const catalogIds = new Set((catalog?.[provider]?.models || []).map((m) => m.id));
    const defaults = DEFAULT_CHECKED[provider] || [];
    if (catalogIds.size === 0) return new Set(defaults);
    return new Set(defaults.filter((id) => catalogIds.has(id)));
  };

  const snapshotSelection = () => {
    const next = emptyLists();
    PROVIDERS.forEach((provider) => {
      next[provider.id] = [...selectedFor(provider.id)];
    });
    return next;
  };

  const emitChange = (next) => {
    if (typeof onChange === 'function') onChange(next);
  };

  const toggleModel = (provider, modelId, checked) => {
    const next = snapshotSelection();
    const current = new Set(next[provider]);
    if (checked) current.add(modelId);
    else current.delete(modelId);
    next[provider] = [...current];
    emitChange(next);
  };

  const setAll = (provider, ids, checked) => {
    const next = snapshotSelection();
    next[provider] = checked ? [...ids] : [];
    emitChange(next);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Available models
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Lists every chat model the saved (or typed) provider key can access. Check the ones
            Novo hosted AI may offer to customers. Unchecked models are never sent to users.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => loadCatalog(true)}
          disabled={loading || !canLoad}
        >
          {loadedOnce ? 'Refresh models' : 'Load models'}
        </Button>
      </Stack>

      {!canLoad && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Save an OpenAI, Gemini, Claude, DeepSeek, Kimi, or GLM API key above to load that
          provider&apos;s live model catalog.
        </Alert>
      )}
      {error && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {PROVIDERS.map((provider) => {
          const result = catalog?.[provider.id];
          const models = Array.isArray(result?.models) ? result.models : [];
          const query = (filter[provider.id] || '').trim().toLowerCase();
          const visible = query
            ? models.filter(
                (m) =>
                  m.id.toLowerCase().includes(query) ||
                  String(m.displayName || '').toLowerCase().includes(query)
              )
            : models;
          const selected = selectedFor(provider.id);
          const selectedCount = models.filter((m) => selected.has(m.id)).length;
          const hasKey =
            Boolean(typed[provider.id] && typed[provider.id].trim()) ||
            Boolean(configured[provider.id]);

          return (
            <Grid item xs={12} md={4} key={provider.id}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1.5,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {provider.label}
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      models.length > 0
                        ? `${selectedCount} of ${models.length}`
                        : hasKey
                          ? 'Not loaded'
                          : 'No key'
                    }
                    color={selectedCount > 0 ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </Stack>

                {result && result.ok === false && result.error && (
                  <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
                    {result.error}
                  </Alert>
                )}

                <TextField
                  size="small"
                  fullWidth
                  label="Filter"
                  value={filter[provider.id]}
                  onChange={(e) =>
                    setFilter((prev) => ({ ...prev, [provider.id]: e.target.value }))
                  }
                  disabled={models.length === 0}
                  sx={{ mb: 1 }}
                />

                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setAll(provider.id, models.map((m) => m.id), true)}
                    disabled={models.length === 0}
                  >
                    Select all
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setAll(provider.id, [], false)}
                    disabled={models.length === 0}
                  >
                    Clear
                  </Button>
                </Stack>

                <Box sx={{ overflowY: 'auto', maxHeight: 320, pr: 0.5 }}>
                  {loading && models.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  ) : visible.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      {hasKey
                        ? loadedOnce
                          ? 'No matching models.'
                          : 'Click Load models to fetch this catalog.'
                        : 'Add an API key to list models.'}
                    </Typography>
                  ) : (
                    visible.map((model) => (
                      <FormControlLabel
                        key={model.id}
                        sx={{ display: 'flex', alignItems: 'flex-start', mr: 0, mb: 0.25 }}
                        control={
                          <Checkbox
                            size="small"
                            checked={selected.has(model.id)}
                            onChange={(e) => toggleModel(provider.id, model.id, e.target.checked)}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" sx={{ lineHeight: 1.2 }}>
                              {model.displayName || model.id}
                            </Typography>
                            {model.displayName && model.displayName !== model.id && (
                              <Typography variant="caption" color="text.secondary">
                                {model.id}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    ))
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
}
